import {useState} from 'react'
import {Pressable, type TextStyle, View} from 'react-native'

import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Text} from '#/components/Typography'
import {
  ASSIST_CANCEL_LABEL,
  ASSIST_CONFIRM_LABEL,
  ASSIST_TITLE,
  ASSIST_WAITING_LINE,
  assistHeadline,
  assistIntentFace,
  assistIntentsFor,
  assistPanelMode,
  assistPrompt,
  assistThinkingLine,
  describeAssistSuggestion,
} from '../assistPanel'
import {
  algToSq,
  type ChessColor,
  type ChessG,
  type ChessMove,
  type ChessPieceType,
  parseFen,
  pieceGlyph,
  PROMOTION_PIECES,
  sqToAlg,
} from '../chess'
import {
  type AssistInfo,
  type AssistIntent,
  type AssistSuggestion,
  type GameCtx,
  type PlayerInfo,
} from '../gameClient'
import {useFlashHint} from './useFlashHint'

/**
 * Fixed board colors (independent of app theme) so the glyphs always read.
 * TWO-TONE NEUTRAL GREY (2026-07-25): a plain grey board so the colour contrast
 * lives entirely on the PIECES (red vs blue below). Mid-toned greys let a strong
 * red and a strong blue both read on either square.
 */
const DARK_SQUARE = '#8a8a8a'
const LIGHT_SQUARE = '#c4c4c4'
const LAST_MOVE_TINT = 'rgba(255, 255, 0, 0.4)'
const SELECT_TINT = 'rgba(255, 255, 0, 0.55)'
const CHECK_TINT = 'rgba(220, 40, 40, 0.55)'

/**
 * ACCESSIBILITY ASSIST highlight. GREEN is the one hue the board is not already
 * using (pieces are red/blue, squares grey, last-move yellow), so a suggested
 * move can never be confused with anything else on screen. It is deliberately
 * NOT colour-only: the two squares also get a thick solid ring, and the
 * destination gets a large filled marker, so the suggestion survives colour
 * blindness and a low-contrast screen.
 */
const ASSIST_TINT = 'rgba(31, 157, 77, 0.55)'
const ASSIST_RING = '#0f7a37'

/**
 * ASSIST PANEL colours are FIXED, like the board's — the player this exists for
 * may be running any theme at any brightness, and a theme-tinted "subtle"
 * surface is exactly how these buttons went unseen the first time. White on
 * #0f7a37 is 5.4:1 (AA at any size, AAA at this size); the dark text on the
 * pale panel is far higher again.
 */
const ASSIST_PANEL_BG = '#eef5f0'
const ASSIST_PANEL_BORDER = '#0f7a37'
const ASSIST_PANEL_TEXT = '#12261a'
const ASSIST_BTN_BG = '#0f7a37'
const ASSIST_BTN_BORDER = '#0a5526'
const ASSIST_BTN_TEXT = '#ffffff'
/** Tap target for the three intent buttons. Bigger than the 44pt platform
 *  minimum on purpose: this is the button for the player with the least
 *  precision, and it must be findable without hunting. */
const ASSIST_BTN_MIN_HEIGHT = 96

/**
 * Piece identity is COLOUR CONTRAST, not black/white (Elliott's pick): seat '0'
 * (chess White) renders solid RED, seat '1' (Black) solid BLUE — clearly red vs
 * clearly blue, deep enough to look intentional, both readable on both grey
 * squares. FLAT — NO drop shadow, halo, bevel, or outline of any kind. The a11y
 * label + status subline say "red"/"blue" to match what is on screen.
 */
const RED_PIECE = '#cf3030'
const BLUE_PIECE = '#2f5ecf'
function pieceStyle(color: ChessColor, size: number): TextStyle[] {
  return [
    {
      // Glyph is 0.90 of the cell (25% larger than the previous 0.72). lineHeight
      // is the FULL cell so the taller glyph stays vertically centred and never
      // overflows the square (lineHeight >= fontSize, capped at the cell height);
      // horizontal centring comes from the cell's justify/align.
      fontSize: size * 0.9,
      lineHeight: size,
      textAlign: 'center',
      color: color === 'w' ? RED_PIECE : BLUE_PIECE,
    },
  ]
}

/** Seat '0' plays white, seat '1' black. */
const colorOfSeat = (seat: string | null): ChessColor | null =>
  seat === '0' ? 'w' : seat === '1' ? 'b' : null

/**
 * The tappable chess board plus status line. Purely presentational: the FEN
 * in the state frame is the whole board, destinations come from the frame's
 * `legalMoves` (server-computed live, pseudo-legal in the mock), and taps
 * report a from/to (+ promotion) move up — full rules never run here. When a
 * destination is a promotion (several legalMoves entries same from/to) a tiny
 * picker overlays the board, queen first. Last move and check highlight.
 * Strings are plain literals — custom (non-Bluesky) surface, no Lingui.
 */
export function ChessBoard({
  G,
  ctx,
  players,
  seat,
  hotSeat = false,
  boardSize,
  onMove,
  onNewGame,
  assist,
}: {
  G: ChessG & {legalMoves: ChessMove[]}
  ctx: GameCtx
  players: PlayerInfo[]
  /** The seat this client holds (null = spectating). */
  seat: string | null
  /** Mock hot-seat: taps act for whichever color's turn it is. */
  hotSeat?: boolean
  /** Rendered edge length of the square board, in px. */
  boardSize: number
  onMove: (from: string, to: string, promotion?: string) => void
  onNewGame?: () => void
  /** ACCESSIBILITY ASSIST (absent = the feature is off for this player, which
   *  is the default for everybody). The board shows three big buttons on this
   *  player's turn, highlights whatever the server recommends, and plays it on
   *  one confirming tap — it never moves on its own. */
  assist?: {
    info: AssistInfo | null
    suggestion: AssistSuggestion | null
    pending: AssistIntent | null
    agentName?: string | null
    onRequest: (intent: AssistIntent) => void
    onDismiss: () => void
  }
}) {
  const t = useTheme()
  const [selected, setSelected] = useState<number | null>(null)
  const {hint, flashHint} = useFlashHint()
  const [promotionChoice, setPromotionChoice] = useState<{
    from: string
    to: string
  } | null>(null)

  const {squares, active} = parseFen(G.fen)
  const over = ctx.gameover ?? null
  // NEVER render a raw seat id ("1 is thinking…"): an unfilled seat gets its
  // own truthful status below; any other unnamed seat reads as "Player N".
  const seatFilled = (id: string) => players.some(p => p.id === id)
  const nameOf = (id: string) =>
    players.find(p => p.id === id)?.name ?? `Player ${Number(id) + 1}`

  const myColor = colorOfSeat(seat)
  const myTurn = hotSeat || (myColor !== null && myColor === active)
  const interactive = over === null && myTurn
  const actingColor: ChessColor = hotSeat ? active : (myColor ?? active)

  const destinations =
    interactive && selected !== null
      ? G.legalMoves.filter(m => m.from === sqToAlg(selected))
      : []

  const lastFrom = G.lastMove ? algToSq(G.lastMove.from) : null
  const lastTo = G.lastMove ? algToSq(G.lastMove.to) : null

  // ---- accessibility assist ------------------------------------------------
  const assistMode = assistPanelMode({
    assist: assist?.info ?? null,
    seat,
    myTurn,
    gameover: over !== null,
    pending: assist?.pending ?? null,
    suggestion: assist?.suggestion ?? null,
  })
  const suggestion =
    assistMode === 'suggestion' ? (assist?.suggestion ?? null) : null
  const assistFrom = suggestion ? algToSq(suggestion.move.from) : null
  const assistTo = suggestion ? algToSq(suggestion.move.to) : null
  /** Play the suggested move — the ONE place a suggestion turns into a move. */
  const playSuggestion = () => {
    if (!suggestion) return
    setSelected(null)
    setPromotionChoice(null)
    onMove(suggestion.move.from, suggestion.move.to, suggestion.move.promotion)
    assist?.onDismiss()
  }
  // Check marks the side to move's king.
  let checkedKingSq: number | null = null
  if (G.check && over === null) {
    for (let i = 0; i < 64; i++) {
      const p = squares[i]
      if (p?.type === 'k' && p.color === active) checkedKingSq = i
    }
  }

  // "You" gets its own grammar ("Your turn", "You win") — the viewer's row in
  // the mock roster is literally named that when signed out.
  const isYou = (id: string) =>
    (seat === id && !hotSeat) || nameOf(id) === 'You'
  const status = over
    ? over.winner !== null
      ? isYou(over.winner)
        ? 'You win!'
        : `${nameOf(over.winner)} wins!`
      : "It's a draw."
    : `${
        !seatFilled(ctx.currentPlayer)
          ? // The turn sits on a seat NOBODY holds — say so; implying a
            // nonexistent opponent is "thinking" hides a hung match.
            'Waiting for a player to join'
          : isYou(ctx.currentPlayer)
            ? 'Your turn'
            : seat !== null && !myTurn
              ? `${nameOf(ctx.currentPlayer)} is thinking…`
              : `${nameOf(ctx.currentPlayer)}'s turn`
      } — ${active === 'w' ? 'red' : 'blue'}${G.check ? ' — check!' : ''}`

  const onSquarePress = (i: number) => {
    if (over !== null) return
    if (!myTurn) {
      flashHint(
        seat === null
          ? 'You’re watching this match'
          : !seatFilled(ctx.currentPlayer)
            ? 'No opponent yet — waiting for someone to join'
            : `Waiting for ${nameOf(ctx.currentPlayer)} — it’s their turn`,
      )
      return
    }
    // A suggestion is on the board: tapping either highlighted square plays it.
    // Tapping the piece Bob pointed at IS the confirming tap — the most obvious
    // gesture should be the one that works.
    if (suggestion && (i === assistFrom || i === assistTo)) {
      playSuggestion()
      return
    }
    setPromotionChoice(null)
    const alg = sqToAlg(i)
    const dests = destinations.filter(m => m.to === alg)
    if (selected !== null && dests.length > 0) {
      if (dests.some(m => m.promotion)) {
        // Several wire moves share this from/to (one per promotion piece):
        // let the player pick which piece the pawn becomes.
        setPromotionChoice({from: dests[0].from, to: alg})
      } else {
        onMove(dests[0].from, alg)
      }
      setSelected(null)
      return
    }
    const piece = squares[i]
    if (piece && piece.color === actingColor) {
      // A piece with zero entries in legalMoves (blocked, or pinned on the
      // live server) must not fail silently — say why the tap did nothing.
      if (!G.legalMoves.some(m => m.from === alg)) {
        flashHint(
          G.check
            ? 'That piece can’t help — you must get out of check'
            : 'That piece has no legal moves right now',
        )
        setSelected(null)
        return
      }
      setSelected(i === selected ? null : i)
    } else {
      setSelected(null)
    }
  }

  const onPromote = (piece: ChessPieceType) => {
    if (!promotionChoice) return
    onMove(promotionChoice.from, promotionChoice.to, piece)
    setPromotionChoice(null)
  }

  const cellSize = Math.floor(boardSize / 8)

  // The three labels share the panel's width, and "Surprise" is the long one:
  // scale the type to the panel instead of clipping it on a narrow phone. Never
  // below 14 — this whole block exists to be readable.
  const assistLabelSize = Math.max(14, Math.min(20, Math.round(boardSize / 21)))

  /**
   * ACCESSIBILITY ASSIST block. Rendered ABOVE the board (2026-07-27): under
   * the board it sat below the fold of the game pane on a phone — the pane is
   * a shrinkable scroll area with the chat lane beneath it — so the player it
   * was built for never saw it and reported the buttons "missing". The board
   * is the biggest thing on screen and needs no help being found; these
   * buttons do. The suggestion highlight is still on the board directly below,
   * so the words and the highlight stay in one glance.
   */
  const assistBlock =
    assistMode === 'hidden' ? null : (
      <View
        testID="assistPanel"
        style={[
          a.w_full,
          a.gap_sm,
          a.px_sm,
          a.py_sm,
          a.rounded_md,
          {
            maxWidth: cellSize * 8,
            backgroundColor: ASSIST_PANEL_BG,
            borderWidth: assistMode === 'suggestion' ? 4 : 3,
            borderColor: ASSIST_PANEL_BORDER,
          },
        ]}>
        <Text
          testID="assistTitle"
          style={[
            a.text_sm,
            a.font_bold,
            a.text_center,
            {color: ASSIST_PANEL_BORDER, letterSpacing: 1},
          ]}>
          {ASSIST_TITLE}
        </Text>
        {assistMode === 'waiting' ? (
          <Text
            testID="assistWaiting"
            style={[
              a.text_md,
              a.text_center,
              a.py_xs,
              {color: ASSIST_PANEL_TEXT, opacity: 0.75},
            ]}>
            {ASSIST_WAITING_LINE}
          </Text>
        ) : assistMode === 'choose' ? (
          <>
            <Text
              style={[
                a.text_md,
                a.font_bold,
                a.text_center,
                {color: ASSIST_PANEL_TEXT},
              ]}>
              {assistPrompt(assist?.agentName ?? null)}
            </Text>
            <View style={[a.flex_row, a.gap_sm]}>
              {assistIntentsFor(assist?.info ?? null).map(intent => {
                const face = assistIntentFace(intent)
                return (
                  <Pressable
                    key={intent}
                    testID={`assist-${intent}`}
                    accessibilityRole="button"
                    accessibilityLabel={face.label}
                    accessibilityHint={face.hint}
                    onPress={() => assist?.onRequest(intent)}
                    style={[
                      a.flex_1,
                      a.align_center,
                      a.justify_center,
                      a.gap_xs,
                      a.rounded_md,
                      a.px_xs,
                      {
                        minHeight: ASSIST_BTN_MIN_HEIGHT,
                        paddingVertical: 10,
                        backgroundColor: ASSIST_BTN_BG,
                        borderWidth: 2,
                        borderColor: ASSIST_BTN_BORDER,
                      },
                    ]}>
                    <Text
                      style={{
                        fontSize: assistLabelSize * 1.6,
                        lineHeight: assistLabelSize * 1.9,
                      }}>
                      {face.emoji}
                    </Text>
                    {/* No numberOfLines: a wrapped label is readable, an
                        ellipsised one ("Surpr…") is not. */}
                    <Text
                      style={[
                        a.font_bold,
                        a.text_center,
                        {
                          fontSize: assistLabelSize,
                          lineHeight: assistLabelSize * 1.25,
                          color: ASSIST_BTN_TEXT,
                        },
                      ]}>
                      {face.label}
                    </Text>
                  </Pressable>
                )
              })}
            </View>
          </>
        ) : assistMode === 'thinking' ? (
          <Text
            testID="assistThinking"
            accessibilityLiveRegion="polite"
            style={[
              a.text_lg,
              a.font_bold,
              a.text_center,
              a.py_md,
              {color: ASSIST_PANEL_TEXT},
            ]}>
            {assistThinkingLine(assist?.pending ?? 'attack')}
          </Text>
        ) : suggestion ? (
          <>
            <Text
              testID="assistReason"
              accessibilityLiveRegion="polite"
              style={[
                a.text_md,
                a.font_bold,
                a.text_center,
                {color: ASSIST_PANEL_TEXT},
              ]}>
              {assistHeadline(suggestion)}
            </Text>
            <Pressable
              testID="assistConfirm"
              accessibilityRole="button"
              accessibilityLabel={ASSIST_CONFIRM_LABEL}
              accessibilityHint={describeAssistSuggestion(suggestion)}
              onPress={playSuggestion}
              style={[
                a.align_center,
                a.justify_center,
                a.rounded_md,
                a.px_md,
                {
                  minHeight: 72,
                  backgroundColor: ASSIST_BTN_BG,
                  borderWidth: 2,
                  borderColor: ASSIST_BTN_BORDER,
                },
              ]}>
              <Text style={[a.text_xl, a.font_bold, {color: ASSIST_BTN_TEXT}]}>
                {ASSIST_CONFIRM_LABEL}
              </Text>
            </Pressable>
            <Pressable
              testID="assistDismiss"
              accessibilityRole="button"
              accessibilityLabel={ASSIST_CANCEL_LABEL}
              accessibilityHint="Puts the three buttons back so you can ask for a different kind of move"
              onPress={() => assist?.onDismiss()}
              style={[a.align_center, a.justify_center, {minHeight: 48}]}>
              <Text
                style={[a.text_md, a.font_bold, {color: ASSIST_PANEL_BORDER}]}>
                {ASSIST_CANCEL_LABEL}
              </Text>
            </Pressable>
          </>
        ) : null}
      </View>
    )

  return (
    <View style={[a.align_center, a.gap_sm]}>
      <Text
        testID="gameStatus"
        style={[a.text_lg, a.font_bold, t.atoms.text]}
        accessibilityLiveRegion="polite">
        {status}
      </Text>

      {/* Fixed-height subline so hints never shift the board mid-play. */}
      <View style={[a.align_center, a.justify_center, {minHeight: 20}]}>
        {hint ? (
          <Text
            testID="boardHint"
            style={[a.text_sm, a.font_bold, {color: t.palette.negative_500}]}
            accessibilityLiveRegion="polite">
            {hint}
          </Text>
        ) : null}
      </View>

      {assistBlock}

      <View
        style={[
          a.rounded_md,
          a.overflow_hidden,
          a.border,
          t.atoms.border_contrast_medium,
          {width: cellSize * 8, height: cellSize * 8},
        ]}>
        {Array.from({length: 8}, (_, row) => (
          <View key={row} style={[a.flex_row]}>
            {Array.from({length: 8}, (_, col) => {
              const i = row * 8 + col
              const piece = squares[i]
              const dark = (row + col) % 2 === 1
              const alg = sqToAlg(i)
              const isSelected = selected === i && piece !== null
              const isDest = destinations.some(m => m.to === alg)
              // The assist highlight OUTRANKS every other tint: when Bob has
              // pointed at a move, that is the only thing on the board the
              // player should have to look at.
              const isAssist = i === assistFrom || i === assistTo
              const tint = isAssist
                ? ASSIST_TINT
                : checkedKingSq === i
                  ? CHECK_TINT
                  : isSelected
                    ? SELECT_TINT
                    : lastFrom === i || lastTo === i
                      ? LAST_MOVE_TINT
                      : null
              return (
                <Pressable
                  key={i}
                  testID={`ch-sq-${alg}`}
                  accessibilityRole="button"
                  accessibilityLabel={
                    (piece
                      ? `${alg}, ${piece.color === 'w' ? 'red' : 'blue'} ${piece.type}`
                      : `${alg}, empty`) +
                    (i === assistFrom
                      ? ', suggested piece to move'
                      : i === assistTo
                        ? ', suggested destination'
                        : '')
                  }
                  accessibilityHint={
                    isAssist
                      ? 'Plays the suggested move'
                      : 'Selects a piece or moves the selected piece here'
                  }
                  disabled={over !== null}
                  onPress={() => onSquarePress(i)}
                  style={[
                    a.align_center,
                    a.justify_center,
                    {
                      width: cellSize,
                      height: cellSize,
                      backgroundColor: dark ? DARK_SQUARE : LIGHT_SQUARE,
                    },
                  ]}>
                  {tint ? (
                    <View
                      style={[a.absolute, a.inset_0, {backgroundColor: tint}]}
                    />
                  ) : null}
                  {/* Thick ring on both suggested squares — the part of the
                      highlight that does NOT depend on seeing the colour. */}
                  {isAssist ? (
                    <View
                      testID={`ch-assist-${i === assistFrom ? 'from' : 'to'}-${alg}`}
                      pointerEvents="none"
                      style={[
                        a.absolute,
                        a.inset_0,
                        {
                          borderWidth: Math.max(3, Math.round(cellSize * 0.09)),
                          borderColor: ASSIST_RING,
                        },
                      ]}
                    />
                  ) : null}
                  {piece ? (
                    <Text style={pieceStyle(piece.color, cellSize)}>
                      {pieceGlyph(piece)}
                    </Text>
                  ) : i === assistTo ? (
                    // Big filled marker on an empty destination — far more
                    // visible than the small legal-move dot.
                    <View
                      style={{
                        width: cellSize * 0.44,
                        height: cellSize * 0.44,
                        borderRadius: cellSize * 0.22,
                        backgroundColor: ASSIST_RING,
                      }}
                    />
                  ) : isDest ? (
                    <View
                      testID={`ch-dest-${alg}`}
                      style={{
                        width: cellSize * 0.3,
                        height: cellSize * 0.3,
                        borderRadius: cellSize * 0.15,
                        backgroundColor: 'rgba(20, 20, 20, 0.35)',
                      }}
                    />
                  ) : null}
                </Pressable>
              )
            })}
          </View>
        ))}
      </View>

      {promotionChoice ? (
        <View
          testID="promotionPicker"
          style={[
            a.flex_row,
            a.align_center,
            a.gap_sm,
            a.px_md,
            a.py_sm,
            a.rounded_md,
            a.border,
            t.atoms.border_contrast_medium,
            t.atoms.bg,
          ]}>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            Promote to
          </Text>
          {PROMOTION_PIECES.map(p => (
            <Pressable
              key={p}
              testID={`promote-${p}`}
              accessibilityRole="button"
              accessibilityLabel={`Promote to ${p}`}
              accessibilityHint="Completes the move, promoting the pawn to this piece"
              onPress={() => onPromote(p)}
              style={[a.px_xs]}>
              <Text style={pieceStyle(actingColor, 40)}>
                {pieceGlyph({color: actingColor, type: p})}
              </Text>
            </Pressable>
          ))}
        </View>
      ) : null}

      {onNewGame == null ? null : over ? (
        <Button
          testID="newGameBtn"
          label="New game"
          color="primary"
          size="small"
          onPress={onNewGame}>
          <ButtonText>New game</ButtonText>
        </Button>
      ) : (
        <Button
          testID="newGameBtn"
          label="Restart game"
          color="secondary"
          size="small"
          onPress={onNewGame}>
          <ButtonText>Restart</ButtonText>
        </Button>
      )}
    </View>
  )
}
